const GA_ID = "G-D26LEQETC2";

/** Google Ads conversion account (gtag.js "AW-" id). */
export const ADS_ID = "AW-833671038";

/**
 * Google Ads conversion label for the conversion action named
 * "BokaDirekt bokningsklick" in Google Ads (the action was recreated, so this
 * label replaces the older, now-dead HoDaCOTU_tscEP6ew40D one).
 * Fired from the delegated outbound listener whenever a visitor clicks through
 * to BokaDirekt — see `track-outbound.ts`.
 */
export const ADS_BOOKING_SEND_TO = `${ADS_ID}/nF8kCJiQtNwcEP6ew40D`;


let started = false;

/**
 * Loads Google Analytics off the critical path.
 *
 * gtag.js is ~180 KB and was previously loaded from the document head, competing
 * with the LCP image and app bundle on mobile. We queue events immediately via the
 * dataLayer stub and only fetch the script once the page is idle or the user
 * interacts, whichever comes first.
 */
export function loadAnalytics() {
  if (typeof window === "undefined" || started) return;
  started = true;

  window.dataLayer = window.dataLayer || [];
  // gtag.js only recognises dataLayer entries that are `arguments` objects.
  // Pushing a real Array (e.g. `push([...args])`) makes gtag.js treat the entry
  // as an ordinary dataLayer message and silently ignore it — that is why no
  // config/event command ever reached GA4 or Google Ads. Keep `arguments`.
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).gtag = gtag;

  gtag("js", new Date());
  // Cross-domain measurement: bookings hand off to BokaDirekt. Without the
  // linker the GA4 session breaks at the domain boundary and BokaDirekt shows
  // up as a referral, which destroys source attribution for Google Ads.
  //
  // NOTE (manual step, cannot be done in code): bokadirekt.se must ALSO be
  // added as an unwanted referral in the GA4 admin UI —
  // Admin -> Data streams -> (web stream) -> Configure tag settings ->
  // List unwanted referrals -> add "bokadirekt.se".
  gtag("config", GA_ID, {
    send_page_view: true,
    linker: { domains: ["tailorsbarber.se", "www.bokadirekt.se", "bokadirekt.se"] },
  });
  // Google Ads conversion account. Shares the same gtag.js load as GA4, so no
  // second script tag is needed; Consent Mode v2 (set in __root.tsx / the CMP)
  // still governs whether anything is stored or transmitted.
  //
  // conversion_linker + url_passthrough keep the gclid alive when ad_storage is
  // denied (no cookie): the click id is carried in the URL instead, so a
  // conversion can still be attributed. ads_data_redaction strips ad
  // identifiers from the ping while consent is denied.
  gtag("set", "url_passthrough", true);
  gtag("set", "ads_data_redaction", true);
  gtag("config", ADS_ID, {
    send_page_view: false,
    conversion_linker: true,
  });

  const inject = () => {
    if (document.getElementById("ga-script")) return;
    const s = document.createElement("script");
    s.id = "ga-script";
    s.async = true;
    // IMPORTANT: the loader id MUST be the GA4 id (G-), not the Ads id (AW-).
    // Do NOT "fix" this to ADS_ID.
    //
    // gtag.js loads the container configuration for whichever id is in the URL.
    // AW-833671038 resolves to a Google tag ("BD - Åhléns City") that belongs to
    // bokadirekt.se, whose destinations are G-WNMKEET04F and AW-833671038. That
    // container knows nothing about G-D26LEQETC2, so loading with the Ads id made
    // the subsequent gtag("config", GA_ID) request a destination the container
    // does not include: GA4 never initialised, no _ga cookie was set and zero
    // events reached the property. Loading with the GA4 id initialises GA4
    // correctly, and Google Ads works as an additional destination via its own
    // gtag("config", ADS_ID) above.
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
  };

  // Paid traffic loads the tag immediately: these visitors carry a gclid, are
  // the only ones whose conversions Google Ads can attribute, and often click
  // straight through to BokaDirekt before a lazy tag would have loaded.
  const params = new URLSearchParams(window.location.search);
  const isPaid =
    params.has("gclid") ||
    params.has("gbraid") ||
    params.has("wbraid") ||
    params.get("utm_medium") === "cpc" ||
    params.get("utm_medium") === "ppc" ||
    params.get("utm_source") === "google_ads";

  if (isPaid) {
    inject();
    return;
  }

  const events = ["pointerdown", "keydown", "touchstart", "scroll"] as const;
  const fire = () => {
    events.forEach((e) => window.removeEventListener(e, fire));
    inject();
  };
  events.forEach((e) => window.addEventListener(e, fire, { once: true, passive: true }));

  // Fallback for visitors who never interact. Kept long so the script never
  // competes with the initial render / hydration work.
  window.setTimeout(fire, 10000);
}

